import {
  getAppliedFilterChips,
  getParcelFilterDefinitions,
  getListingFilterDefinitions,
  getFilterGroupIdForField,
  expandLegacyParsedRangeBands,
} from '../data/parcelFilterContract';
import {
  NL_SEARCH_INTENTS,
  NL_SEARCH_SCHEMA_VERSION,
  NL_SEARCH_SHARED_SCHEMA_VERSION,
  NL_SEARCH_CAD_SCHEMA_VERSION,
} from '../data/nlSearchConstants';
import { CAD_FILTER_DEFS, getCadAppliedFilterChips } from '../../Showroom/data/cadFilterContract';
import { sanitizeCadListFilters } from '../../Showroom/utils/cadFilterMatch';
import { SURFACE_HECTARE_THRESHOLD_M2 } from './surfaceDisplay';

/**
 * Normaliza una frase para matchear el diccionario del stub (acentos, m², espacios).
 * @param {string} query
 * @returns {string}
 */
export function normalizeNlQuery(query) {
  return String(query || '')
    .trim()
    .toLowerCase()
    .replace(/m²/gi, 'm2')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
}

/**
 * @param {unknown} schemaVersion
 * @returns {boolean}
 */
export function isKnownNlSearchSchema(schemaVersion) {
  return schemaVersion == null || schemaVersion === NL_SEARCH_SCHEMA_VERSION;
}

/**
 * @param {unknown} schemaVersion
 * @returns {boolean}
 */
export function isKnownSharedListNlSearchSchema(schemaVersion) {
  return schemaVersion === NL_SEARCH_SHARED_SCHEMA_VERSION;
}

/**
 * @param {unknown} schemaVersion
 * @returns {boolean}
 */
export function isKnownCadListNlSearchSchema(schemaVersion) {
  return schemaVersion === NL_SEARCH_CAD_SCHEMA_VERSION;
}

/**
 * El parser a veces manda calle/barrio con la forma "del otro componente".
 * El tile recorta por el string de catastro: hay que setear el chip igual.
 * El parse ya debería mandar el valor del gazetteer/tile, no Nominatim.
 * @param {object} def
 * @param {unknown} value
 * @returns {unknown}
 */
function coerceParsedFilterValue(def, value) {
  if (def.field === 'neighborhood_name' && typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : null;
  }
  if (def.field === 'street_name' && Array.isArray(value)) {
    const first = value.find((item) => typeof item === 'string' && item.trim());
    return first ? String(first).trim() : null;
  }
  if ((def.field === 'surface' || def.field === 'valuation') && Array.isArray(value)) {
    return expandLegacyParsedRangeBands(def.field, value);
  }
  return value;
}

/**
 * `valuation` / `buildable` son números; inundación es `"3"`. Haiku a veces
 * manda el otro tipo: hay que caer en el `value` canónico del contrato.
 * @param {unknown} item
 * @param {Set<unknown>} allowed
 * @returns {unknown|null}
 */
function canonicalizeOptionValue(item, allowed) {
  if (allowed.has(item)) return item;
  if (typeof item === 'string' && item.trim() !== '') {
    const asNumber = Number(item);
    if (Number.isFinite(asNumber) && allowed.has(asNumber)) return asNumber;
  }
  if (typeof item === 'number' && allowed.has(String(item))) return String(item);
  return null;
}

function sanitizeParsedFiltersByDefinitions(filters, definitions) {
  if (!filters || typeof filters !== 'object') return {};

  const defsByField = Object.fromEntries(
    definitions.map((def) => [def.field, def]),
  );
  const sanitized = {};

  Object.entries(filters).forEach(([field, rawValue]) => {
    const def = defsByField[field];
    if (!def || rawValue == null) return;
    const value = coerceParsedFilterValue(def, rawValue);
    if (value == null) return;

    if (def.component === 'multiSelect' || def.component === 'rangeBands') {
      if (!Array.isArray(value)) return;
      const allowed = new Set((def.options || []).map((opt) => opt.value));
      const next = def.optionsFrom === 'map'
        ? value.map((item) => String(item).trim()).filter(Boolean)
        : value.map((item) => canonicalizeOptionValue(item, allowed)).filter((item) => item != null);
      if (next.length) sanitized[field] = next;
      return;
    }

    if (def.component === 'triState') {
      if (value === 0 || value === 1) sanitized[field] = value;
      return;
    }

    if (def.component === 'inlineBoolean') {
      if (value === def.trueValue) sanitized[field] = value;
      return;
    }

    if (def.component === 'textContains') {
      if (typeof value !== 'string') return;
      const trimmed = value.trim();
      if (trimmed.length >= (def.minLength || 1)) sanitized[field] = trimmed;
    }
  });

  return sanitized;
}

/**
 * Queda solo lo que `setFilterValue` del tab Lote entiende.
 * Keys o valores que no están en el contrato se descartan (no rompen el mapa).
 * @param {Record<string, unknown>|null|undefined} filters
 * @returns {Record<string, unknown>}
 */
export function sanitizeParsedLotFilters(filters) {
  return sanitizeParsedFiltersByDefinitions(filters, getParcelFilterDefinitions());
}

/**
 * Lista compartida: Lote ∪ Publicación / unidad, incluido moneda y precio.
 * `listing_price` solo admite keys que mandamos en `listing_price_bands`
 * (nunca `lt50k` ni cortes de otra lista).
 *
 * @param {Record<string, unknown>|null|undefined} filters
 * @param {object} [options]
 * @param {string[]} [options.listingPriceBandKeys]
 * @returns {Record<string, unknown>}
 */
export function sanitizeParsedSharedListFilters(filters, { listingPriceBandKeys = [] } = {}) {
  if (!filters || typeof filters !== 'object') return {};

  const nextFilters = { ...filters };
  if (Array.isArray(nextFilters.listing_currency)) {
    nextFilters.listing_currency = nextFilters.listing_currency.map((item) => (
      String(item || '').trim().toUpperCase()
    ));
  }

  const listingDefs = getListingFilterDefinitions().filter((def) => def.field !== 'listing_price');
  const sanitized = sanitizeParsedFiltersByDefinitions(nextFilters, [
    ...getParcelFilterDefinitions(),
    ...listingDefs,
  ]);

  const allowed = new Set(listingPriceBandKeys);
  if (Array.isArray(nextFilters.listing_price) && allowed.size) {
    const prices = nextFilters.listing_price.filter((item) => allowed.has(item));
    if (prices.length) sanitized.listing_price = prices;
  }

  return sanitized;
}

/**
 * Recorta el eco de `unresolved` para que un input largo o con emoji no rompa el chip.
 * @param {string} text
 * @param {number} [maxChars]
 * @returns {string}
 */
export function truncateNlEcho(text, maxChars = 72) {
  const value = String(text || '').replace(/\s+/g, ' ').trim();
  const chars = [...value];
  if (chars.length <= maxChars) return value;
  return `${chars.slice(0, Math.max(0, maxChars - 1)).join('')}…`;
}

function decorateDisplayItem(item, defsByField) {
  const field = item.field || null;
  const def = field ? defsByField[field] : null;
  const approximate = item.approximate === true || def?.component === 'rangeBands';
  const groupId = item.kind === 'location'
    ? 'ubicacion'
    : getFilterGroupIdForField(field);
  return {
    ...item,
    field,
    approximate,
    groupId,
  };
}

function formatArNumber(value) {
  return new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(value);
}

function formatSurfaceBound(m2) {
  if (m2 == null) return '';
  if (m2 >= SURFACE_HECTARE_THRESHOLD_M2) {
    const ha = m2 / SURFACE_HECTARE_THRESHOLD_M2;
    const text = Number.isInteger(ha) ? String(ha) : formatArNumber(ha);
    return `${text} ha`;
  }
  return `${formatArNumber(m2)} m²`;
}

function formatUsdBound(value) {
  if (value == null) return '';
  return `USD ${formatArNumber(value)}`;
}

function formatBound(def, value) {
  if (value == null) return '';
  if (def.field === 'surface') return formatSurfaceBound(value);
  if (def.field === 'valuation') return formatUsdBound(value);
  if (def.field === 'altura_max_m') return `${formatArNumber(value)} m`;
  return formatArNumber(value);
}

/**
 * Envelope de bandas consecutivas para el dropdown (el panel sigue con un chip
 * por banda). “Más de 5.000 m²” → `≥ 5.000 m²`, no siete filas.
 * @param {object} def
 * @param {unknown[]} selectedValues
 * @returns {string|null}
 */
function summarizeContiguousRangeBands(def, selectedValues) {
  if (!Array.isArray(selectedValues) || selectedValues.length < 2) return null;
  const options = def.options || [];
  const selected = selectedValues
    .map((value) => {
      const index = options.findIndex((opt) => opt.value === value);
      return index >= 0 ? { index, opt: options[index] } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.index - b.index);
  if (selected.length < 2) return null;
  const contiguous = selected.every((item, i) => (
    i === 0 || item.index === selected[i - 1].index + 1
  ));
  if (!contiguous) return null;

  const first = selected[0].opt;
  const last = selected[selected.length - 1].opt;
  const lo = first.min;
  const hi = last.max;

  if (lo == null && hi == null) {
    return `${first.shortLabel || first.label} – ${last.shortLabel || last.label}`;
  }
  if (hi == null) return `≥ ${formatBound(def, lo)}`;
  if (lo == null) return `${last.maxExclusive ? '<' : '≤'} ${formatBound(def, hi)}`;
  return `${formatBound(def, lo)} – ${formatBound(def, hi)}`;
}

function chipValueText(item) {
  const idx = String(item.label || '').indexOf(': ');
  return idx >= 0 ? item.label.slice(idx + 2) : String(item.label || '');
}

/**
 * Pin de lugar + chip de provincia/barrio con el mismo nombre = una sola fila.
 * @param {object} item
 * @param {string} [locationLabel]
 * @returns {boolean}
 */
function isRedundantLocationFilter(item, locationLabel) {
  const locationKey = normalizeNlQuery(locationLabel);
  if (!locationKey) return false;
  if (item.field !== 'province_name' && item.field !== 'neighborhood_name') return false;
  return normalizeNlQuery(chipValueText(item)) === locationKey;
}

/**
 * Si el backend o los chips listan una fila por banda, resumir las contiguas.
 * Bandas no contiguas se dejan una por una. El panel no se toca.
 * @param {Array<object>} items
 * @param {Record<string, object>} defsByField
 * @param {Record<string, unknown>} [filters]
 * @param {string} [locationLabel]
 * @returns {Array<object>}
 */
function collapseRangeBandDisplayItems(items, defsByField, filters, locationLabel) {
  const collapsibleFields = new Set();
  items.forEach((item) => {
    const def = item.field ? defsByField[item.field] : null;
    if (def?.component !== 'rangeBands') return;
    if (summarizeContiguousRangeBands(def, filters?.[item.field])) {
      collapsibleFields.add(item.field);
    }
  });

  const emittedRangeFields = new Set();
  const result = [];
  items.forEach((item) => {
    if (isRedundantLocationFilter(item, locationLabel)) return;

    if (collapsibleFields.has(item.field)) {
      if (emittedRangeFields.has(item.field)) return;
      emittedRangeFields.add(item.field);
      const def = defsByField[item.field];
      const summary = summarizeContiguousRangeBands(def, filters?.[item.field]);
      const fieldLabel = def.shortLabel || def.label;
      result.push(decorateDisplayItem({
        field: item.field,
        label: `${fieldLabel}: ${summary}`,
      }, defsByField));
      return;
    }

    result.push(decorateDisplayItem(item, defsByField));
  });
  return result;
}

/**
 * Labels de chips para el dropdown. Usa `display` del backend si vino;
 * si no, chips del contrato + el lugar.
 * Bandas consecutivas del mismo campo se resumen en una sola fila.
 * Bandas (`rangeBands`) se marcan `approximate` (snap, no el número literal).
 * @param {object} data
 * @param {Record<string, { options?: Array<object> }>} [mapOptionsByField]
 * @returns {Array<{ kind?: string, field?: string, label: string, approximate?: boolean, groupId?: string }>}
 */
export function buildParsedSearchDisplay(data, mapOptionsByField = null) {
  if (isKnownCadListNlSearchSchema(data?.schema_version)) {
    const sanitized = sanitizeCadListFilters(data?.filters || {});
    const rawItems = Array.isArray(data?.display) && data.display.length
      ? data.display
      : getCadAppliedFilterChips(sanitized).map((chip) => ({
        field: chip.field,
        label: chip.label,
      }));
    const defsByField = Object.fromEntries(CAD_FILTER_DEFS.map((def) => [def.field, def]));
    return collapseRangeBandDisplayItems(rawItems, defsByField, sanitized, null);
  }
  const definitions = [...getParcelFilterDefinitions(), ...getListingFilterDefinitions()];
  const defsByField = Object.fromEntries(definitions.map((def) => [def.field, def]));
  const sanitized = sanitizeParsedFiltersByDefinitions(data?.filters || {}, definitions);

  let rawItems;
  if (Array.isArray(data?.display) && data.display.length) {
    rawItems = data.display;
  } else {
    rawItems = [];
    if (data?.location?.label) {
      rawItems.push({ kind: 'location', label: data.location.label });
    }
    getAppliedFilterChips(sanitized, mapOptionsByField).forEach((chip) => {
      rawItems.push({ field: chip.field, label: chip.label });
    });
  }

  return collapseRangeBandDisplayItems(
    rawItems,
    defsByField,
    sanitized,
    data?.location?.label,
  );
}

/**
 * ¿Este intent vuela el mapa?
 * @param {string} intent
 * @returns {boolean}
 */
export function parsedSearchShouldFly(intent) {
  return intent === NL_SEARCH_INTENTS.place || intent === NL_SEARCH_INTENTS.hybrid;
}

/**
 * ¿Este intent reemplaza los filtros de parcela?
 * @param {string} intent
 * @returns {boolean}
 */
export function parsedSearchShouldApplyFilters(intent) {
  return intent === NL_SEARCH_INTENTS.filter || intent === NL_SEARCH_INTENTS.hybrid;
}

const NOT_A_PLACE_TAIL = /^(un\s+|una\s+)?(barrio\s+cerrado|barrio\s+popular|barrio\s+residencial|country|zonas?\s+urbanas?|esquina|venta|alquiler|alquiler\s+temporario|ph|dolares?|pesos)$/i;
const SURFACE_TAIL = /^\d[\d.,\s]*(m2|m²|ha|hectareas?)?\s*$/i;

/**
 * Lugar al final de una frase NL (`… en juana koslay`). Nominatim no geocodifica
 * la frase entera (“lotes mayores a 1000 m2 en …” → 0 hits).
 * @param {string} query
 * @returns {string}
 */
export function placeNameFromNlQuery(query) {
  const text = String(query || '').trim();
  if (!text) return '';

  let last = null;
  const marker = /\ben\s+/gi;
  let match = marker.exec(text);
  while (match) {
    last = match;
    match = marker.exec(text);
  }
  if (!last) return '';

  let tail = text.slice(last.index + last[0].length).trim().replace(/[.,;:]+$/g, '');
  tail = tail.replace(/^(el\s+barrio\s+(de\s+)?|la\s+ciudad\s+de\s+|la\s+provincia\s+de\s+|el\s+municipio\s+de\s+)/i, '').trim();
  if (!tail || tail.length < 4) return '';
  if (NOT_A_PLACE_TAIL.test(normalizeNlQuery(tail))) return '';
  if (SURFACE_TAIL.test(normalizeNlQuery(tail))) return '';
  return tail;
}

/**
 * Convierte `location` del parse a lo que espera `onFlyTo`.
 * Prefiere `bbox` (fitBounds del lugar). `center` es último recurso: un pin,
 * no el envelope de las parcelas que cumplen el filtro.
 * @param {object|null} location
 * @returns {{ lng: number, lat: number, bbox: number[][]|null }|null}
 */
export function flyTargetFromParsedLocation(location) {
  if (!location || typeof location !== 'object') return null;

  if (Array.isArray(location.bbox) && location.bbox.length === 4) {
    const [west, south, east, north] = location.bbox.map(Number);
    if (![west, south, east, north].every(Number.isFinite)) return null;
    const lng = Number(location.center?.lng);
    const lat = Number(location.center?.lat);
    return {
      lng: Number.isFinite(lng) ? lng : (west + east) / 2,
      lat: Number.isFinite(lat) ? lat : (south + north) / 2,
      bbox: [[west, south], [east, north]],
    };
  }

  const lng = Number(location.center?.lng);
  const lat = Number(location.center?.lat);
  if (Number.isFinite(lng) && Number.isFinite(lat)) {
    return { lng, lat, bbox: null };
  }

  return null;
}
